import React from 'react';
import { Link } from 'react-router-dom';

const DocketPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Docket - Task Manager by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: 19 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Docket ("the App"), a task management application built on the Getting
              Things Done (GTD) methodology. Docket is developed and operated by Wolf Technologies
              ("we", "us", "our").
            </p>
            <p>
              We are committed to protecting your privacy and handling your personal data
              responsibly. This Privacy Policy explains what data we collect, how we use it, and
              your rights in relation to that data.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>
              We collect only the minimum data necessary to provide and improve the Docket service.
            </p>
            <h3>Account Information</h3>
            <ul>
              <li><strong>Email address</strong> — used for account creation, authentication, and essential service communications.</li>
              <li><strong>Password</strong> — stored securely via AWS Cognito using industry-standard hashing. We never have access to your plaintext password.</li>
            </ul>
            <h3>Task and Productivity Data</h3>
            <ul>
              <li><strong>Tasks</strong> — titles, notes, due dates, deadlines, tags, checklists, and completion status.</li>
              <li><strong>Projects</strong> — project names, descriptions, and associated tasks.</li>
              <li><strong>Areas of Responsibility</strong> — area names and associated projects.</li>
              <li><strong>Tags</strong> — tag names and associations with tasks and projects.</li>
            </ul>
            <h3>Subscription and Payment Information</h3>
            <ul>
              <li><strong>Subscription status</strong> — your current plan (free, monthly, annual, or lifetime) and trial status.</li>
              <li><strong>Stripe customer ID</strong> — a unique identifier for web payment processing. We do <strong>not</strong> store your credit card number, bank details, or other sensitive payment credentials.</li>
              <li><strong>Apple transaction ID</strong> — a unique identifier to verify iOS purchases.</li>
              <li><strong>Payment history metadata</strong> — subscription start dates, renewal dates, and plan changes.</li>
            </ul>
            <h3>Technical Data</h3>
            <ul>
              <li><strong>Device and browser information</strong> — browser type, operating system, and screen size, collected to ensure the App displays correctly.</li>
              <li><strong>Error and crash logs</strong> — anonymous technical logs to help identify and fix bugs.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We believe in data minimisation. Docket does <strong>not</strong> collect, access, or store:</p>
            <ul>
              <li><strong>Location data</strong> — we do not track your geographical location.</li>
              <li><strong>Contacts</strong> — we do not access your device's address book.</li>
              <li><strong>Health or fitness data</strong></li>
              <li><strong>Browsing history</strong> — we do not track your activity outside the App.</li>
              <li><strong>Photos, camera, or microphone data</strong></li>
              <li><strong>Advertising identifiers</strong> — we do not use advertising tracking or serve ads.</li>
              <li><strong>Biometric data</strong></li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <ul>
              <li><strong>Providing the task management service</strong> — storing and syncing your tasks, projects, areas, and tags.</li>
              <li><strong>Account authentication and security</strong> — using your email and hashed password.</li>
              <li><strong>Processing subscriptions and payments</strong> — using subscription status and Stripe/Apple IDs.</li>
              <li><strong>Sending essential service communications</strong> — using your email address.</li>
              <li><strong>Improving app stability and performance</strong> — using error logs and technical data.</li>
              <li><strong>Responding to support requests</strong> — using your email address and account data.</li>
            </ul>
            <p>We do <strong>not</strong> use your data for advertising, profiling, selling to third parties, or training AI/ML models.</p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <p>
              All data is stored on Amazon Web Services (AWS) infrastructure in the <strong>EU
              West (London) region (eu-west-2)</strong>. Your data remains within the United Kingdom.
            </p>
            <ul>
              <li><strong>Encryption at rest</strong> — all data stored in DynamoDB is encrypted using AWS-managed encryption keys (AES-256).</li>
              <li><strong>Encryption in transit</strong> — all data transmitted between your device and our servers is encrypted using TLS 1.2 or higher.</li>
              <li><strong>Authentication</strong> — handled by AWS Cognito with secure, standards-based salted password hashing.</li>
              <li><strong>IAM authorisation</strong> — API requests are authenticated using AWS IAM Signature Version 4 (SigV4), ensuring only authenticated users can access their own data.</li>
              <li><strong>Least privilege access</strong> — backend services operate with minimum necessary permissions.</li>
              <li><strong>Logical isolation</strong> — each user's data is logically isolated. You can only access your own tasks, projects, and areas.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>We use the following third-party services to operate Docket:</p>
            <ul>
              <li><strong>AWS Cognito</strong> — user registration, authentication, and session management.</li>
              <li><strong>Amazon DynamoDB</strong> — stores your task data, projects, areas, tags, and user profile.</li>
              <li><strong>AWS Lambda</strong> — executes backend application logic.</li>
              <li><strong>Amazon API Gateway</strong> — routes and secures API requests between the App and our backend.</li>
              <li><strong>Stripe</strong> — processes payments for web-based subscriptions. We receive only a customer ID and subscription status — never your card details.</li>
              <li><strong>Apple In-App Purchase</strong> — processes payments for iOS subscriptions. We receive a transaction identifier — Apple handles all payment details.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              <strong>We do not sell, rent, trade, or otherwise share your personal data with third
              parties for their own purposes.</strong>
            </p>
            <p>We share data only in the following limited circumstances:</p>
            <ul>
              <li><strong>Service providers</strong> — with the third-party services listed above, strictly to operate the App.</li>
              <li><strong>Legal obligations</strong> — if required by law, regulation, or valid legal process.</li>
              <li><strong>Safety</strong> — to protect the rights, property, or safety of Wolf Technologies, our users, or the public.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li><strong>Active accounts</strong> — your data is retained for as long as your account is active.</li>
              <li><strong>Account deletion</strong> — you may request deletion by contacting us at <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. All associated data will be permanently deleted within 30 days.</li>
              <li><strong>Inactive accounts</strong> — accounts inactive for more than 24 months may be flagged for deletion. We will notify you by email before any action is taken.</li>
              <li><strong>Backup retention</strong> — automated backups may retain deleted data for up to 30 days, after which it is permanently removed.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the UK GDPR and the Data Protection Act 2018, you have the following rights:
            </p>
            <ul>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — request correction of any inaccurate or incomplete data.</li>
              <li><strong>Erasure</strong> — request deletion of your personal data.</li>
              <li><strong>Restriction</strong> — request that we limit how we use your data.</li>
              <li><strong>Data portability</strong> — request your data in a structured, machine-readable format.</li>
              <li><strong>Objection</strong> — object to our processing of your data in certain circumstances.</li>
              <li><strong>Withdraw consent</strong> — where processing is based on consent, you may withdraw it at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. We will
              respond within 30 days.
            </p>
            <p>
              If you are not satisfied with our response, you have the right to lodge a complaint
              with the Information Commissioner's Office (ICO) at https://ico.org.uk.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last updated" date at the top
              will be revised. For significant changes, we will notify you by email or via an in-app
              notification.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our
              data practices, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
            <p>We aim to respond to all enquiries within 48 hours.</p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default DocketPrivacy;
